import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Button,
  MenuItem,
  Select,
  Typography,
  TextField,
  Divider,
  IconButton,
  Menu,
  Paper,
  Chip,
  Collapse,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import TemplateText from "./TemplateText";
import TemplateImage from "./TemplateImage";
import TemplateTable from "./TemplateTable";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SettingsIcon from "@mui/icons-material/Settings";
import Request from "../Utils/Request/Request";
import { showToast } from "../Utils";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const ComponentTypes = {
  COMPONENT: "component",
};

const DraggableComponent = ({ component, index, moveComponent, ...props }) => {
  const [expanded, setExpanded] = useState(true);
  const [stylesExpanded, setStylesExpanded] = useState(false);
  const [addAnchor, setAddAnchor] = useState(null);
  const [, ref] = useDrag({
    type: ComponentTypes.COMPONENT,
    item: { index },
  });

  const itemTypes = [...new Set(component.items.map((item) => item.type))];
  const blockName = itemTypes.length
    ? itemTypes.map((type) => type.charAt(0).toUpperCase() + type.slice(1)).join(" + ")
    : "Empty block";

  const addItem = (item) => {
    props.updateComponentData(component.indexNo, item);
    setAddAnchor(null);
    setExpanded(true);
  };

  const [, drop] = useDrop({
    accept: ComponentTypes.COMPONENT,
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveComponent(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div ref={(node) => ref(drop(node))}>
      <Box
        sx={{
          border: "1px solid #d7e0e4",
          borderRadius: 2,
          position: "relative",
          background: "#fff",
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(31, 52, 61, 0.05)",
        }}
      >
        <Box sx={{ px: 1.25, py: 1, backgroundColor: "#f7f9fa", borderBottom: expanded ? "1px solid #e3e8eb" : 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <DragIndicatorIcon sx={{ color: "#82939b", cursor: "grab", flexShrink: 0 }} />
            <Typography variant="subtitle2" noWrap title={`Block ${index + 1} · ${blockName}`} sx={{ minWidth: 0, flex: 1 }}>
              Block {index + 1} · {blockName}
            </Typography>
            <Tooltip title={expanded ? "Collapse block" : "Expand block"}><IconButton size="small" onClick={() => setExpanded((value) => !value)} sx={{ flexShrink: 0, transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}><ExpandMoreIcon /></IconButton></Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.75 }}>
            <Chip size="small" label={`${component.size}/12`} title="Invoice width" sx={{ height: 22, fontSize: 11 }} />
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1, ml: 0.5 }}>{component.align}</Typography>
            <Tooltip title="Move up"><span><IconButton size="small" disabled={index === 0} onClick={() => moveComponent(index, index - 1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Move down"><span><IconButton size="small" disabled={index === props.componentCount - 1} onClick={() => moveComponent(index, index + 1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Duplicate block"><IconButton size="small" onClick={() => props.duplicateComponent(index)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Add content"><IconButton size="small" color="primary" onClick={(event) => setAddAnchor(event.currentTarget)}><AddIcon /></IconButton></Tooltip>
          </Box>
          <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
            <MenuItem onClick={() => addItem({ type: "text", value: "" })}>Text fields</MenuItem>
            <MenuItem onClick={() => addItem({ type: "image", value: "" })}>Image</MenuItem>
            <MenuItem onClick={() => addItem({ type: "table", value: [], tableType: "" })}>Table</MenuItem>
          </Menu>
        </Box>
        <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
        {component.items.map((item, idx) => (
          <React.Fragment key={idx}>
            <Button size="small" variant="outlined" color="error"
              onClick={() => props.removeComponentType(index, idx)}
              sx={{ mt: 1, mb: 2 }}
            >
              Remove {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
            </Button>
            {item.type === "text" && (
              <>
              <TemplateText
                value={
                  Array.isArray(item.value)
                    ? item.value
                    : [{ label: "", value: "" }]
                }
                onChange={(newValue) => {
                  const updatedItems = [...component.items];
                  updatedItems[idx] = { ...updatedItems[idx], value: newValue };
                  props.updateComponentText(index, { items: updatedItems });
                }}
              />
              <Divider sx={{ background: "#808b97", marginTop: "2rem" }} />
              </>
            )}

            {item.type === "image" && (
              <>
              <TemplateImage
                value={item}
                onChange={(newItem) => {
                  const updatedItems = [...component.items];
                  updatedItems[idx] = newItem;

                  props.updateComponentImage(index, updatedItems);
                }}
              />
              <Divider sx={{ background: "#808b97", marginTop: "2rem" }} />
              </>
            )}

            {item.type === "table" && (
              <>
              <TemplateTable
                data={item}
                onChange={(data) => {
                  const updatedItems = [...component.items];
                  updatedItems[idx] = { ...updatedItems[idx], ...data };
                  props.updateTabletData(index, { items: updatedItems });
                }}
                tableType={item.tableType}
              />
              <Divider sx={{ background: "#808b97", marginTop: "2rem" }} />
              </>
            )}
            
          </React.Fragment>
        ))}
        <Button size="small" startIcon={<SettingsIcon />} onClick={() => setStylesExpanded((value) => !value)} sx={{ mt: 1, color: "#365b6d" }}>
          {stylesExpanded ? "Hide block styles" : "Block styles"}
        </Button>
        <Collapse in={stylesExpanded}>
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            mt: 1,
            p: 1.5,
            gap: 1.25,
            borderRadius: 1,
            backgroundColor: "#f7f9fa",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mb: 0.5 }}>
              Invoice width
            </Typography>
            <Select
              size="small"
              fullWidth
              value={component.size}
              onChange={(e) => props.updateComponentSize(index, e.target.value)}
            >
              {[...Array(12)].map((_, i) => (
                <MenuItem key={i} value={i + 1}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mb: 0.5 }}>
              Alignment
            </Typography>
            <Select
              size="small"
              fullWidth
              value={component.align}
              onChange={(e) =>
                props.updateComponentFeatures(index, { align: e.target.value })
              }
            >
              <MenuItem value="right">Right</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="left">Left</MenuItem>
            </Select>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mb: 0.5 }}>
              Label weight
            </Typography>
            <Select
              size="small"
              fullWidth
              value={component.fontWeight}
              onChange={(e) =>
                props.updateComponentFeatures(index, {
                  fontWeight: e.target.value,
                })
              }
            >
              <MenuItem value="bold">Bold</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
            </Select>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mb: 0.5 }}>Value weight</Typography>
            <Select
              size="small"
              fullWidth
              value={component.fontWeightValue}
              onChange={(e) =>
                props.updateComponentFeatures(index, {
                  fontWeightValue: e.target.value,
                })
              }
            >
              <MenuItem value="bold">Bold</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
            </Select>
          </Box>
        </Box>
        </Collapse>
        <Box display="flex" alignItems="center" mt={1}>
          <Button size="small" startIcon={<DeleteIcon />} onClick={() => props.removeComponent(index)} sx={{ color: "error.main" }}
          >
            Delete block
          </Button>
        </Box>
        </Box>
        </Collapse>
      </Box>
    </div>
  );
};

const TemplateDesign = ({ duplicate }) => {
  const { id } = useParams(); // Get ID from URL parameters
  const navigate = useNavigate();
  const [templateName, setTemplateName] = useState("");
  const [components, setComponents] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const warnBeforeLeaving = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [isDirty]);

  useEffect(() => {
    if (id) {
      const fetchTemplate = async () => {
        try {
          const response = await Request.get(`/getTemplateById/${id}`);
          console.log("d", response?.data.template_values);

          const parsedComponents = response?.data?.template_values.map((component) => {
            let parsedValue = component.value;
            if (typeof component.value === "string") {
              try {
                parsedValue = JSON.parse(component.value);
              } catch (error) {
                console.warn(`Skipping JSON parse for value:`, component.value);
              }
            }
            return { ...component, value: parsedValue, componentIndex: component.columnIndex };
          });
          
          // Group components by `indexNo`
          const groupedComponents = parsedComponents.reduce((acc, component) => {
            const { indexNo, size, align, fontWeight, fontWeightValue, tableType, type, value, componentIndex } = component;
          
            let parent = acc.find((c) => c.indexNo === indexNo);
            if (!parent) {
              parent = {
                indexNo,
                size,
                align,
                fontWeight,
                fontWeightValue,
                items: [],
              };
              acc.push(parent);
            }
          
            // Push item into `items` array, tracking `componentIndex`
            parent.items.push({
              type,
              value,
              tableType,
              componentIndex, // Keep track of its index within component
            });
          
            return acc;
          }, []);
          
          // Sort `items` inside each component by `componentIndex`
          groupedComponents.forEach((component) => {
            component.items.sort((a, b) => a.componentIndex - b.componentIndex);
          });
          
          // Sort components by `indexNo`
          groupedComponents.sort((a, b) => a.indexNo - b.indexNo);
          
          setTemplateName(response.data.name);
          setComponents(groupedComponents);
          setTemplateId(response.data.id);
          setIsDirty(false);
        } catch (error) {
          console.error("Error fetching template: ", error);
        }
      };

      fetchTemplate();
    }
  }, [id]);

  const updateComponentText = (index, newItem) => {
    setIsDirty(true);
    setComponents((prev) => {
      const updatedComponents = [...prev];
      updatedComponents[index] = {
        ...updatedComponents[index],
        items: newItem.items || [],
      };
      return updatedComponents;
    });
  };

  const updateComponentImage = (index, updatedItems) => {
    setIsDirty(true);
    setComponents((prev) => {
      const updatedComponents = [...prev];
      updatedComponents[index] = {
        ...updatedComponents[index],
        items: updatedItems,
      };
      return updatedComponents;
    });
  };

  const updateComponentSize = (index, size) => {
    setIsDirty(true);
    const newComponents = [...components];
    newComponents[index].size = size;
    setComponents(newComponents);
  };

  const updateTabletData = (index, newItem) => {
    setIsDirty(true);
    setComponents((prev) => {
      const updatedComponents = [...prev];
      updatedComponents[index] = {
        ...updatedComponents[index],
        ...newItem, // ✅ No extra wrapping
      };
      return updatedComponents;
    });
  };

  const updateComponentFeatures = (index, data) => {
    setIsDirty(true);
    const newComponents = [...components];
    newComponents[index] = { ...newComponents[index], ...data };
    setComponents(newComponents);
  };

  const updateComponentData = (indexNo, newData) => {
    setIsDirty(true);
    setComponents((prevComponents) => {
      return prevComponents.map((component) => {
        if (component.indexNo === indexNo) {
          return {
            ...component,
            items: [...component.items, newData], // Add new component type inside items array
          };
        }
        return component;
      });
    });
  };

  const removeComponentType = (indexNo, itemIndex) => {
    setIsDirty(true);
    setComponents((prevComponents) => {
      return prevComponents.map((component) => {
        if (component.indexNo === indexNo) {
          return {
            ...component,
            items: component.items.filter((_, idx) => idx !== itemIndex), // Remove item at index
          };
        }
        return component;
      });
    });
  };

  const addComponent = () => {
    setIsDirty(true);
    setComponents((prevComponents) => {
      const newComponent = {
        indexNo: prevComponents.length,
        size: 12,
        align: "right",
        fontWeight: "normal",
        fontWeightValue: "normal",
        items: [],
        type: "text",
      };
      return [...prevComponents, newComponent];
    });
  };

  const removeComponent = (index) => {
    if (!window.confirm("Delete this block and all fields inside it?")) return;
    setIsDirty(true);
    setComponents((prevComponents) =>
      prevComponents
        .filter((_, componentIndex) => componentIndex !== index)
        .map((component, componentIndex) => ({ ...component, indexNo: componentIndex }))
    );
  };

  const validateComponents = () => {
    return components.some((component) => component.items.length > 0);
  };

  const saveTemplate = async () => {
    setComponents((prevComponents) => {
      const validComponents = prevComponents.filter((c) => c.items.length > 0);

      const rearrangedComponents = validComponents.map((component, index) => ({
        ...component,
        indexNo: index,
      }));

      return rearrangedComponents;
    });

    if (!templateName.trim()) {
      showToast("error", "Template name cannot be empty.");
      return;
    }

    if (!validateComponents()) {
      showToast("error", "At least one valid component is required.");
      return;
    }

    setIsSubmitting(true);

    const formattedComponents = components.flatMap((component) =>
      component.items.map((item, idx) => ({
        indexNo: component.indexNo, // Keep original index
        type: item.type, // Use item type (text, image, table)
        value: item.value, // Use item value
        size: component.size, // Keep size from parent
        tableType: item.tableType || null, // Keep tableType if available
        align: component.align, // Keep alignment
        fontWeight: component.fontWeight, // Keep fontWeight
        fontWeightValue: component.fontWeightValue, // Keep fontWeightValue
        componentIndex: idx, 
      }))
    );

    if (formattedComponents.length === 0) {
      showToast("error", "At least one valid component is required.");
      setIsSubmitting(false);
      return;
    }

    const endpoint =
      templateId && duplicate !== true ? "/updateTemplate" : "/saveTemplate";
    const payload = templateId
      ? { id: templateId, name: templateName, components: formattedComponents }
      : { name: templateName, components: formattedComponents };
console.log(payload);

    try {
      await Request.post(endpoint, payload);
      setIsSubmitting(false);
      setIsDirty(false);
      showToast("success", "Template Saved.");
      navigate("/templateList");
    } catch (err) {
      setIsSubmitting(false);
      console.log("Error: ", err);
      if (err.response?.status === 401) {
        showToast("error", "Unauthorized user.");
      } else if (err.response?.status === 409) {
        showToast("error", err.response?.data?.error);
      } else {
        showToast("error", "Something went wrong.");
      }
    }
  };

  const moveComponent = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= components.length) return;
    setIsDirty(true);
    const updatedComponents = [...components];
    const [movedComponent] = updatedComponents.splice(fromIndex, 1);
    updatedComponents.splice(toIndex, 0, movedComponent);

    updatedComponents.forEach((component, idx) => {
      component.indexNo = idx;
    });

    setComponents(updatedComponents);
  };

  const duplicateComponent = (index) => {
    setIsDirty(true);
    setComponents((current) => {
      const duplicateBlock = JSON.parse(JSON.stringify(current[index]));
      const next = [...current];
      next.splice(index + 1, 0, duplicateBlock);
      return next.map((component, componentIndex) => ({
        ...component,
        indexNo: componentIndex,
      }));
    });
  };

  const visibleComponents = components.filter((component) => component.type !== null);

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ px: { xs: 2, md: 4 }, pb: 5, backgroundColor: "#f4f6f7", minHeight: "100vh" }}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            py: 2,
            backgroundColor: "rgba(244, 246, 247, .96)",
            backdropFilter: "blur(8px)",
            borderBottom: "1px solid #dfe5e8",
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Template Name"
                value={templateName}
                onChange={(e) => { setTemplateName(e.target.value); setIsDirty(true); }}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid item xs={12} lg={9}>
            <Box
              sx={{
                position: "sticky",
                top: 73,
                zIndex: 9,
                mb: 2,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                backgroundColor: "rgba(244, 246, 247, .96)",
                backdropFilter: "blur(8px)",
                borderBottom: "1px solid #dfe5e8",
              }}
            >
              <Box>
                <Typography variant="h6">Template blocks</Typography>
                <Typography variant="body2" color="text.secondary">Drag blocks to reorder them. Their widths share a 12-column row.</Typography>
              </Box>
              <Button variant="outlined" startIcon={<AddIcon />} sx={{color:"#365b6d", borderColor:"#365b6d"}} onClick={addComponent}>
                Add Block
              </Button>
            </Box>
            <Grid container spacing={2}>
          {components.map((component, index) => (
            component.type !== null &&
            <Grid item xs={component.size} key={index}>
              <DraggableComponent
                component={component}
                index={index}
                moveComponent={moveComponent}
                updateComponentData={updateComponentData}
                removeComponent={removeComponent}
                updateComponentSize={updateComponentSize}
                updateTabletData={updateTabletData}
                updateComponentText={updateComponentText}
                updateComponentImage={updateComponentImage}
                updateComponentFeatures={updateComponentFeatures}
                removeComponentType={removeComponentType}
                duplicateComponent={duplicateComponent}
                componentCount={components.length}
              />
            </Grid>
          ))}
            </Grid>
            {visibleComponents.length === 0 && (
              <Paper variant="outlined" sx={{ p: 5, textAlign: "center", borderStyle: "dashed" }}>
                <Typography variant="h6">Start with your first block</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Blocks can contain text fields, images, or invoice tables.</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={addComponent} sx={{ backgroundColor: "#365b6d" }}>Add Block</Button>
              </Paper>
            )}
          </Grid>
          <Grid item xs={12} lg={3}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                position: "sticky",
                top: 88,
                maxHeight: "calc(100vh - 112px)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">Layout preview</Typography>
              <Typography variant="caption" color="text.secondary">A compact map of the saved invoice grid.</Typography>
              <Box sx={{ mt: 2, p: 1.25, minHeight: 240, flex: 1, overflowY: "auto", backgroundColor: "#fff", border: "1px solid #d9e0e4", boxShadow: "0 3px 12px rgba(20,42,51,.08)" }}>
                <Grid container spacing={0.75}>
                  {visibleComponents.map((component, index) => (
                    <Grid item xs={component.size} key={`preview-${index}`}>
                      <Box sx={{ minHeight: 44, p: 0.75, borderRadius: 0.5, backgroundColor: "#eaf0f2", border: "1px solid #cfdbdf", overflow: "hidden" }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 700 }} noWrap>{index + 1}. {component.items.map((item) => item.type).join(" + ") || "Empty"}</Typography>
                        <Typography sx={{ fontSize: 8, color: "text.secondary" }}>{component.size}/12</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                <Chip
                  size="small"
                  color={isDirty ? "warning" : "success"}
                  variant="outlined"
                  label={isDirty ? "Unsaved" : "Saved"}
                />
                <Button
                  onClick={saveTemplate}
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{ backgroundColor: "#365b6d", flex: 1, whiteSpace: "nowrap" }}
                >
                  {isSubmitting ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Saving…</> : "Save Template"}
                </Button>
                <Button variant="outlined" onClick={() => navigate("/templateList")}>Cancel</Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </DndProvider>
  );
};

export default TemplateDesign;
